import Resource from 'Resources/Resource';
import Salesboard from 'Resources/Salesboard';
import ProjectBoard from 'App/resources/ProjectBoard';
import EventBus from 'App/services/EventBus';
import config from 'App/babel/config';
import { globalTracker } from 'Helpers/Tracker';
import { modules } from 'App/helpers/segmentHelper';
import { STYLE } from 'Store/reducers/SystemNotificationReducer';
import { Feature, isAvailable } from 'App/services/FeatureHelperStandalone';

/*
	Standalone replacement for the write half of Tools.ListView / Tools.ListViewService (WEB-7834).

	A 1:1 port of the save/delete paths of upsales/common/resources/listView.js plus the type
	dispatch that used to live in upsales/common/services/listViewService.js. That Angular service
	now delegates to saveListView/deleteListView below, so the Angular and React worlds keep ONE
	implementation of list-view persistence and therefore one source of listView.* events —
	AppService.setListViews and Salesboard.js both depend on those landing exactly once.

	get/find/findUdo are deliberately NOT ported yet: their only remaining consumers sit in the
	Angular tree (plus babel/controllers/AdminAccountSettingsCtrl.js) and they need the udo parser
	and AppService plumbing, which would double the size of this change for no decouple win.

	Notifications go through window.NotificationService rather than a static import of
	App/babel/NotificationService: that module imports the store, and this one is imported by
	Store/reducers/SharedViewsReducer, so the static import would close a require cycle through
	babel/store/index.ts. Same reason (and same workaround) as babel/resources/Resource.js.
*/

type ListViewLike = {
	id?: number | string | null;
	type?: string;
	standard?: boolean;
	[key: string]: any;
};

export type ListViewOptions = {
	skipEvent?: boolean;
	skipNotification?: boolean;
	skipErrorNotification?: boolean;
};

type ListViewResponse = { data: any; metadata?: any };

const EVENT_PREFIX = 'listView';

/*
	Reproduction of ResourceHelper.onSave/onSaveError/onDelete/onDeleteError
	(upsales/common/resources/resourceHelper.js) with the defaults listView.js set at its lines
	36-44. Only the branches listView can actually reach are kept: no caller passes an eventPrefix
	function, analyticsProps or notificationType, and the Angular resource called onSaveError /
	onDeleteError WITHOUT the error object, so ResourceHelper's parseError always returned null and
	the error body was always the constant below. Do not "improve" that into showing the server
	error — it would change what the toast says.
*/
const notify = (title: string, body: string, isError: boolean) => {
	window.NotificationService.add({
		title,
		body,
		style: isError ? STYLE.ERROR : STYLE.SUCCESS,
		icon: isError ? 'times' : 'check'
	});
};

const broadcast = (event: string, data: unknown) => {
	if (config.ENV === 'DEV' && console && console.log) {
		console.log('broadcasting:', `${EVENT_PREFIX}.${event}`);
	}
	EventBus.broadcast(`${EVENT_PREFIX}.${event}`, data);
};

const onSave = (options: ListViewOptions, data: unknown, isNew: boolean) => {
	globalTracker.track(`${isNew ? 'Created' : 'Updated'} ${EVENT_PREFIX}`, { module: modules.listView });

	if (!options.skipEvent) {
		broadcast(isNew ? 'added' : 'updated', data);
	}
	if (!options.skipNotification) {
		notify(isNew ? 'default.saved' : 'default.updated', isNew ? 'saved.listView' : 'updated.listView', false);
	}
};

const onSaveError = (options: ListViewOptions) => {
	if (!options.skipErrorNotification) {
		notify('default.error', 'saveError.listView', true);
	}
};

const onDelete = (options: ListViewOptions, data: unknown) => {
	if (!options.skipEvent) {
		broadcast('deleted', data);
	}
	if (!options.skipNotification) {
		notify('default.deleted', 'deleted.listView', false);
	}
};

const onDeleteError = (options: ListViewOptions) => {
	if (!options.skipErrorNotification) {
		notify('default.error', 'deleteError.listView', true);
	}
};

/*
	$resource serialised request bodies with angular.toJson, which drops every $$-prefixed key at any
	depth (angular.js:1020); JSON.stringify, which axios uses, keeps them. The Angular list pages hand
	their raw view objects to save — listFilters/Filter2.js passes the ngRepeat item itself and sets
	$$dirty / $$saving on it — and the old /list endpoint persists the request body verbatim into
	redis (base/src/core/listview/model.js: `redis.hset(hash, id, JSON.stringify(fixColumns(data)))`).
	Without this those keys would start being stored and served back. Reproducing toJson, not fixing.
*/
const stripAngularInternals = (view: ListViewLike): ListViewLike =>
	JSON.parse(JSON.stringify(view, (key, value) => (key.startsWith('$$') ? undefined : value)));

const toUdoEntityType = (entityType: string) => {
	const nr = parseInt(entityType[entityType.length - 1]);
	return !isNaN(nr) && nr > 1 && nr < 5 ? `userdefobj${nr}` : 'userdefobj';
};

type RequestTarget = {
	customerId: number;
	entityType: string;
	visibilityType: string;
	id?: number | string | null;
	useOldResource: boolean;
};

/*
	The two $resource url templates from listView.js:
		Resource    = URL + API + customer + '/listViews/:entityType/:id'
		OldResource = URL + API + customer + '/list/:visibilityType/:entityType/:id'
	$resource drops a :param it has no value for and appends every param the template does NOT
	name as a query string — which is why visibilityType is a query param on the new url and a
	path segment on the old one. Split as { _url, path } because Resource._getUrl joins the two
	with a slash and would otherwise leave a trailing one.
*/
const buildRequest = ({ customerId, entityType, visibilityType, id, useOldResource }: RequestTarget) => {
	if (useOldResource) {
		return id != null
			? { path: String(id), opts: { _url: `${customerId}/list/${visibilityType}/${entityType}` } }
			: { path: entityType, opts: { _url: `${customerId}/list/${visibilityType}` } };
	}

	return id != null
		? { path: String(id), opts: { _url: `${customerId}/listViews/${entityType}`, params: { visibilityType } } }
		: { path: entityType, opts: { _url: `${customerId}/listViews`, params: { visibilityType } } };
};

class ListViewInstance {
	private resource: ListViewResource;
	private customerId: number;
	visibilityType: string | null = null;
	entityType: string | null = null;

	constructor(resource: ListViewResource, customerId: number) {
		this.resource = resource;
		this.customerId = customerId;
	}

	setVisibilityType(type: string) {
		this.visibilityType = type;
		return this;
	}

	setEntityType(type: string) {
		if (isAvailable(Feature.SHARED_VIEWS) && type === 'client') {
			type = 'account';
		}

		this.entityType = type;
		return this;
	}

	private useOldResource() {
		return !isAvailable(Feature.SHARED_VIEWS) || this.entityType?.toLowerCase() === 'salesboard';
	}

	save(view: ListViewLike, options: ListViewOptions = {}): Promise<ListViewResponse> {
		if (!this.entityType) {
			throw new Error('No entity type set for list view resource');
		}
		if (!this.visibilityType) {
			throw new Error('No visibility type set for list view resource');
		}
		// The Angular original returned `$q.defer().reject('No view')` here, which evaluates to
		// undefined and blows up in the caller's .then(). Unreachable, kept verbatim.
		if (!view) {
			return undefined as unknown as Promise<ListViewResponse>;
		}

		// Only save gates the udo rewrite on the feature flag; delete does not. See delete().
		if (!isAvailable(Feature.SHARED_VIEWS) && this.entityType.indexOf('userDefinedObject') === 0) {
			this.entityType = toUdoEntityType(this.entityType);
		}

		const isNew = !view.id;
		const promise = this.resource._save(
			{
				customerId: this.customerId,
				entityType: this.entityType,
				visibilityType: this.visibilityType,
				id: view.id,
				useOldResource: this.useOldResource()
			},
			view
		);

		promise
			.then(res => {
				onSave(options, res.data, isNew);
				return res;
			})
			.catch(() => {
				onSaveError(options);
			});

		this.visibilityType = null;
		this.entityType = null;

		return promise;
	}

	delete(view: ListViewLike, options: ListViewOptions = {}): Promise<ListViewResponse | undefined> {
		if (!this.entityType) {
			throw new Error('No entity type set for list view resource');
		}

		/*
			Pre-existing asymmetry, deliberately preserved: save() only rewrites
			userDefinedObject<n> -> userdefobj<n> when SHARED_VIEWS is OFF, delete() always does.
			It looks like a bug but the endpoints have lived with it, so changing it here would be
			a behaviour change smuggled into a decouple.
		*/
		if (this.entityType.indexOf('userDefinedObject') === 0) {
			this.entityType = toUdoEntityType(this.entityType);
		}

		// delete() never nulls the instance state either — again matching the Angular original.
		return this.resource
			._delete({
				customerId: this.customerId,
				entityType: this.entityType,
				visibilityType: 'whatever',
				id: view.id,
				useOldResource: this.useOldResource()
			})
			.then(res => {
				onDelete(options, view);
				return res;
			})
			.catch(() => {
				onDeleteError(options);
				return undefined;
			});
	}
}

class ListViewResource extends Resource {
	constructor() {
		// Every request overrides _url, but Resource requires a base url.
		// No notificationTitle: notifications are fired by the helpers above, not by the base class.
		super('listViews');
	}

	customer(customerId: number) {
		return new ListViewInstance(this, customerId);
	}

	_save(target: RequestTarget, view: ListViewLike): Promise<ListViewResponse> {
		const { path, opts } = buildRequest(target);
		const requestOpts = { ...opts, methodName: 'save' };
		const body = stripAngularInternals(view);

		const request = target.id
			? this._putRequest(path, body, requestOpts)
			: this._postRequest(path, body, requestOpts);

		return request.then((res: { data: ListViewResponse }) => res.data);
	}

	_delete(target: RequestTarget): Promise<ListViewResponse> {
		const { path, opts } = buildRequest(target);

		return this._deleteRequest(path, { ...opts, methodName: 'delete' }).then(
			(res: { data: ListViewResponse }) => res.data
		);
	}
}

const ListView = new ListViewResource();

/*
	Port of ListViewService.save/delete (upsales/common/services/listViewService.js). Kept on the
	same (type, view, customerId, publicType, options) signature so both the React call sites and
	the Angular service can hand their arguments straight through.
*/
export const saveListView = (
	type: string,
	view: ListViewLike,
	customerId: number,
	publicType?: boolean,
	options?: ListViewOptions
): Promise<any> => {
	if (!isAvailable(Feature.SHARED_VIEWS)) {
		type = type === 'account' ? 'client' : type;
	}

	if (type === 'salesboard') {
		return Salesboard.save(view).then((res: ListViewResponse) => res.data);
	}

	if (type === 'projectBoard') {
		view.standard = false;
		return ProjectBoard.save(view).then((res: ListViewResponse) => res.data);
	}

	return ListView.customer(customerId)
		.setEntityType(type)
		.setVisibilityType(publicType ? 'public' : 'own')
		.save(view, options)
		.then(res => res.data);
};

/*
	Pre-existing quirk, deliberately preserved: the underlying delete is fired and forgotten, and
	the returned promise resolves on the next tick regardless of whether it succeeded. Callers can
	therefore never observe a delete failure — Salesboards.tsx already removes the board from local
	state on that basis.
*/
export const deleteListView = (type: string, view: ListViewLike, customerId: number): Promise<string> => {
	if (!isAvailable(Feature.SHARED_VIEWS)) {
		type = type === 'account' ? 'client' : type;
	}

	if (type === 'salesboard') {
		Salesboard.delete(view.id);
	} else if (type === 'projectBoard') {
		ProjectBoard.delete(view.id);
	} else {
		ListView.customer(customerId).setEntityType(type).delete(view);
	}

	return new Promise(resolve => setTimeout(() => resolve('deleted'), 0));
};

export default ListView;
